// utils/formatters.ts

export const formatCurrency = (value: number | string | undefined | null): string => {
  if (value === null || value === undefined || value === '') {
    return 'R$ 0,00';
  }
  
  // Se for string, converte para número
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return 'R$ 0,00';
  }
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(numValue);
};

export const formatPercentage = (value: number | string | undefined | null): string => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  
  // Se for string, converte para número
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0';
  }
  
  return numValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatCpfCnpj = (value: string | null | undefined): string => {
  // Verificar se o valor existe e é uma string
  if (!value || typeof value !== 'string') {
    return '-';
  }
  
  // Remove todos os caracteres não numéricos
  const numbers = value.replace(/\D/g, '');
  
  if (numbers.length === 11) {
    // Formato CPF: 000.000.000-00
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (numbers.length === 14) {
    // Formato CNPJ: 00.000.000/0000-00
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  } else if (numbers.length === 15) {
    // Formato CNPJ com zero à esquerda: 00.000.000/0000-00
    // Remove o zero à esquerda se existir e aplica a formatação de CNPJ
    const cnpjNumbers = numbers.startsWith('0') ? numbers.substring(1) : numbers;
    if (cnpjNumbers.length === 14) {
      return cnpjNumbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  }
  
  // Se não for CPF nem CNPJ válido, retorna o valor original ou '-' se estiver vazio
  return numbers.length > 0 ? value : '-';
};

export const formatDate = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  if (isNaN(date.getTime())) {
    return 'Data inválida';
  }
  
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatDateTime = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  if (isNaN(date.getTime())) {
    return 'Data inválida';
  }
  
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatPhone = (phone: string): string => {
  // Remove todos os caracteres não numéricos
  const numbers = phone.replace(/\D/g, '');
  
  if (numbers.length === 10) {
    // Formato: (00) 0000-0000
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else if (numbers.length === 11) {
    // Formato: (00) 00000-0000
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  
  // Se não for um telefone válido, retorna o valor original
  return phone;
};

// Convert various date inputs into an ISO-like timestamp using the
// America/Sao_Paulo timezone (Brasília). We produce an RFC3339 string with
// the -03:00 offset so backend receives explicit timezone information.
export const toSaoPauloTimestamp = (value: any): string | undefined => {
  if (!value) return undefined;

  // Helper to build YYYY-MM-DDTHH:mm:ss-03:00
  const buildOffsetString = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());
    // Brasília is currently UTC-3 and does not use DST in modern rules.
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}-03:00`;
  };

  // If already a Date
  if (value instanceof Date) {
    // Convert the Date to the Sao Paulo equivalent by using the date's time
    // but formatting with -03:00 offset. We assume the Date represents the
    // local/parsed moment; building string directly from the Date fields.
    return buildOffsetString(value);
  }

  if (typeof value === 'string') {
    // ISO-like string that already contains timezone info -> normalize
    if (/^\d{4}-\d{2}-\d{2}T/.test(value) && /[zZ]|[+-]\d{2}:?\d{2}$/.test(value)) {
      // parse and then reformat to -03:00
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return buildOffsetString(parsed);
    }

    // YYYY-MM-DD -> treat as date in Sao Paulo at midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const parts = value.split('-');
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 0, 0, 0);
      return buildOffsetString(d);
    }

    // DD/MM/YYYY -> convert
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [dStr, mStr, yStr] = value.split('/');
      const d = new Date(Number(yStr), Number(mStr) - 1, Number(dStr), 0, 0, 0);
      return buildOffsetString(d);
    }

    // Fallback: try parsing with Date and format
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) return buildOffsetString(dt);
    return undefined;
  }

  return undefined;
};